import { useState, FormEvent, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TextInputModern, Button } from '@pavanhiro/common-ui';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://zoauth-auth-engine-owht3p65vq-uc.a.run.app';

interface SignupForm {
  email: string;
  password: string;
  confirmPassword: string;
  organization: string;
  subdomain: string;
  plan: string;
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<SignupForm>({
    email: '',
    password: '',
    confirmPassword: '',
    organization: '',
    subdomain: '',
    plan: 'free',
  });

  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (planParam && ['free', 'business', 'trial'].includes(planParam)) {
      setForm(prev => ({ ...prev, plan: planParam }));
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<any>(null);

  const validate = (): string | null => {
    if (!form.email) return 'Email address is required';
    if (!/\S+@\S+\.\S+/.test(form.email)) return 'Please enter a valid email address';
    if (!form.password) return 'Password is required';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    if (!form.organization.trim()) return 'Organization name is required';
    if (!form.subdomain) return 'Subdomain is required';
    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(form.subdomain)) {
      return 'Subdomain must be lowercase alphanumeric, 2-63 characters';
    }
    return null;
  };

  const handleSubdomainChange = (value: string) => {
    // Auto-sanitize: lowercase, remove spaces
    const sanitized = value.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '');
    setForm({ ...form, subdomain: sanitized });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/tenants/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          organization: form.organization,
          subdomain: form.subdomain,
          plan: form.plan,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        const msg =
          data?.error_description || data?.message || data?.error || 'Signup failed. Please try again.';
        throw new Error(msg);
      }

      setResult(data);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success && result) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">ZoAuth</div>
        </div>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>
            Welcome to ZoAuth!
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, marginBottom: '16px' }}>
            Your organization <strong>{result.tenant_code}</strong> has been created.
          </p>
          <div
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
              Your OIDC Issuer URL:
            </div>
            <code
              style={{
                display: 'block',
                background: 'var(--bg-tertiary)',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                color: 'var(--text-primary)',
                wordBreak: 'break-all',
              }}
            >
              {result.issuer_url}
            </code>
          </div>
          <Link to={`/login?email=${encodeURIComponent(form.email)}`}>
            <Button variant="primary" size="large" style={{ width: '100%' }}>
              Continue to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">ZoAuth</div>
        <div className="auth-subtitle">Create your free account</div>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '8px 0 0' }}>
          Live OIDC issuer at <strong>your-org.zoauth.com</strong> — up in 30 seconds
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <TextInputModern
          name="email"
          label="Work email"
          variant="email"
          value={form.email}
          onChange={(value) => setForm({ ...form, email: value })}
          placeholder="you@company.com"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          autoComplete="email"
        />

        <TextInputModern
          name="password"
          label="Password"
          variant="password"
          value={form.password}
          onChange={(value) => setForm({ ...form, password: value })}
          placeholder="Min. 8 characters"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          passwordToggle
          autoComplete="new-password"
        />

        <TextInputModern
          name="confirmPassword"
          label="Confirm password"
          variant="password"
          value={form.confirmPassword}
          onChange={(value) => setForm({ ...form, confirmPassword: value })}
          placeholder="Repeat password"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          passwordToggle
          autoComplete="new-password"
        />

        <TextInputModern
          name="organization"
          label="Organization name"
          variant="text"
          value={form.organization}
          onChange={(value) => setForm({ ...form, organization: value })}
          placeholder="Acme Corp"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          autoComplete="organization"
        />

        <div>
          <TextInputModern
            name="subdomain"
            label="Subdomain"
            variant="text"
            value={form.subdomain}
            onChange={handleSubdomainChange}
            placeholder="acme"
            required
            size="lg"
            styleType="outline"
            labelType="stacked"
            helperText="Becomes your OIDC issuer URL"
            error={error || undefined}
          />
          <div
            style={{
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              marginTop: '4px',
              fontFamily: 'monospace',
            }}
          >
            {form.subdomain ? `${form.subdomain}.zoauth.com` : 'your-org.zoauth.com'}
          </div>
        </div>

        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '16px 0' }}>
          <div style={{ 
            background: 'var(--bg-secondary)', 
            padding: '12px', 
            borderRadius: '8px',
            marginBottom: '12px',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
              {form.plan === 'business' ? '🚀 Business Plan' : form.plan === 'trial' ? '⏱️ 14-day Trial' : '🆓 Free Plan'}
            </div>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: '#48bb78' }}>✓</span> 
              {form.plan === 'business' ? '100k MAU + SAML SSO' : form.plan === 'trial' ? 'All Business features included' : '10k MAU included'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: '#48bb78' }}>✓</span> 
              {form.plan === 'business' || form.plan === 'trial' ? 'Priority support' : 'No credit card required'}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
            Want different features? <Link to="/pricing" style={{ color: '#667eea' }}>View all plans</Link>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={loading}
          loading={loading}
          style={{ width: '100%', marginTop: '8px' }}
        >
          {loading ? 'Creating account…' : 'Create free account'}
        </Button>
      </form>

      <div className="auth-footer">
        <span>Already have an account?</span>
        <Link to="/login" className="footer-link">
          Sign in
        </Link>
      </div>
    </div>
  );
}
